import type { RequestedSupportingItem } from './RequestedSupportingItem'
import {
  PersonalInfoSupportingItemInput,
  type PersonalInfoSupportingItemInputProps,
} from './personal-info/PersonalInfoSupportingItemInput'
import {
  SpousalInfoSupportingItemInput,
  type SpousalInfoSupportingItemInputProps,
} from './spousal-info/SpousalInfoSupportingItemInput'
import {
  DependentsInfoSupportingItemInput,
  type DependentsInfoSupportingItemInputProps,
} from './dependents-info/DependentsInfoSupportingItemInput'
import {
  PersonalTaxRefundMethodSupportingItemInput,
  type PersonalTaxRefundMethodSupportingItemInputProps,
} from './refund-methods/PersonalTaxRefundMethodSupportingItemInput'
import {
  BusinessInfoSupportingItemInput,
  type BusinessInfoSupportingItemInputProps,
} from './business-info/BusinessInfoSupportingItemInput'
import {
  BusinessTaxRefundMethodSupportingItemInput,
  type BusinessTaxRefundMethodSupportingItemInputProps,
} from './refund-methods/BusinessTaxRefundMethodSupportingItemInput'
import {
  DocumentSupportingItemInput,
  type DocumentSupportingItemInputProps,
} from './documents/DocumentSupportingItemInput'
import {
  NotesSupportingItemInput,
  type NotesSupportingItemInputProps,
} from './notes/NotesSupportingItemInput'

/**
 * Common members for all supporting item inputs.
 */
export interface SupportingItemInputBase {
  /**
   * Whether the item is considered completed based on the input's
   * current state.
   */
  readonly isCompleted: boolean
}

/**
 * Types of supporting item inputs.
 */
export type SupportingItemInput =
  | PersonalInfoSupportingItemInput
  | SpousalInfoSupportingItemInput
  | DependentsInfoSupportingItemInput
  | PersonalTaxRefundMethodSupportingItemInput
  | BusinessInfoSupportingItemInput
  | BusinessTaxRefundMethodSupportingItemInput
  | DocumentSupportingItemInput
  | NotesSupportingItemInput

/**
 * Props for {@link SupportingItemInput}.
 */
export type SupportingItemInputProps =
  | PersonalInfoSupportingItemInputProps
  | SpousalInfoSupportingItemInputProps
  | DependentsInfoSupportingItemInputProps
  | PersonalTaxRefundMethodSupportingItemInputProps
  | BusinessInfoSupportingItemInputProps
  | BusinessTaxRefundMethodSupportingItemInputProps
  | DocumentSupportingItemInputProps
  | NotesSupportingItemInputProps

/**
 * Match arms used for {@link matchSupportingItemInput}.
 */
interface MatchSupportingItemInputArmsCore<R> {
  personalInfo: (
    item: RequestedSupportingItem<PersonalInfoSupportingItemInput>,
  ) => R
  spousalInfo: (
    item: RequestedSupportingItem<SpousalInfoSupportingItemInput>,
  ) => R
  dependentsInfo: (
    item: RequestedSupportingItem<DependentsInfoSupportingItemInput>,
  ) => R
  personalTaxRefundMethod: (
    item: RequestedSupportingItem<PersonalTaxRefundMethodSupportingItemInput>,
  ) => R
  businessInfo: (
    item: RequestedSupportingItem<BusinessInfoSupportingItemInput>,
  ) => R
  businessTaxRefundMethod: (
    item: RequestedSupportingItem<BusinessTaxRefundMethodSupportingItemInput>,
  ) => R
  document: (item: RequestedSupportingItem<DocumentSupportingItemInput>) => R
  notes: (item: RequestedSupportingItem<NotesSupportingItemInput>) => R
}

/**
 * Match arms used for {@link matchSupportingItemInput}.
 */
export type MatchSupportingItemInputArms<R> =
  | MatchSupportingItemInputArmsCore<R>
  | (Partial<MatchSupportingItemInputArmsCore<R>> & {
      default: (item: RequestedSupportingItem) => R
    })

/**
 * Matches on the supporting item type.
 *
 * @param item
 * @param arms
 */
export function matchSupportingItemInput<T>(
  item: RequestedSupportingItem,
  arms: MatchSupportingItemInputArms<T>,
): T {
  if (item.input instanceof DocumentSupportingItemInput && arms.document) {
    return arms.document(
      item as RequestedSupportingItem<DocumentSupportingItemInput>,
    )
  }

  if (
    item.input instanceof PersonalInfoSupportingItemInput &&
    arms.personalInfo
  ) {
    return arms.personalInfo(
      item as RequestedSupportingItem<PersonalInfoSupportingItemInput>,
    )
  }

  if (
    item.input instanceof SpousalInfoSupportingItemInput &&
    arms.spousalInfo
  ) {
    return arms.spousalInfo(
      item as RequestedSupportingItem<SpousalInfoSupportingItemInput>,
    )
  }

  if (
    item.input instanceof DependentsInfoSupportingItemInput &&
    arms.dependentsInfo
  ) {
    return arms.dependentsInfo(
      item as RequestedSupportingItem<DependentsInfoSupportingItemInput>,
    )
  }

  if (
    item.input instanceof PersonalTaxRefundMethodSupportingItemInput &&
    arms.personalTaxRefundMethod
  ) {
    return arms.personalTaxRefundMethod(
      item as RequestedSupportingItem<PersonalTaxRefundMethodSupportingItemInput>,
    )
  }

  if (
    item.input instanceof BusinessInfoSupportingItemInput &&
    arms.businessInfo
  ) {
    return arms.businessInfo(
      item as RequestedSupportingItem<BusinessInfoSupportingItemInput>,
    )
  }

  if (
    item.input instanceof BusinessTaxRefundMethodSupportingItemInput &&
    arms.businessTaxRefundMethod
  ) {
    return arms.businessTaxRefundMethod(
      item as RequestedSupportingItem<BusinessTaxRefundMethodSupportingItemInput>,
    )
  }

  if (item.input instanceof NotesSupportingItemInput && arms.notes) {
    return arms.notes(item as RequestedSupportingItem<NotesSupportingItemInput>)
  }

  if ('default' in arms) {
    return arms.default(item)
  }

  // Should be unreachable.
  throw new Error('Unhandled supporting item input type')
}
